import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Paperclip, X, Image as ImageIcon } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  listAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from "@/lib/announcements-api";
import { fmtDate } from "@/lib/format-date";

type Draft = {
  title: string;
  description: string;
  image_url: string | null;
  imageFile: File | null;
};

const EMPTY: Draft = { title: "", description: "", image_url: null, imageFile: null };

/** Rich-text toolbar shown in the description editor. */
const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ header: [1, 2, 3, false] }],
    [{ color: [] }, { background: [] }],
    ["blockquote", "code-block"],
    ["link"],
    ["clean"],
  ],
};
const QUILL_FORMATS = [
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "header",
  "color",
  "background",
  "blockquote",
  "code-block",
  "link",
];

/** Quill emits `<p><br></p>` for an empty editor — treat that as no content. */
function isDescriptionEmpty(html: string): boolean {
  return html.replace(/<(.|\n)*?>/g, "").trim().length === 0;
}

/** Strip HTML tags and decode entities to a single-line plain-text preview. */
function toPlainText(html: string): string {
  const el = document.createElement("textarea");
  el.innerHTML = html.replace(/<[^>]*>/g, " ");
  return el.value.replace(/\s+/g, " ").trim();
}

/** Time-only (HH:MM) from an ISO timestamp; empty when unparseable. */
function fmtTime(input: string | null | undefined): string {
  if (!input) {
    return "";
  }
  const d = new Date(input);
  if (isNaN(d.getTime())) {
    return "";
  }
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export const Route = createFileRoute("/admin/announcements")({ component: Page });

function Page() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>({ ...EMPTY });
  const [createKey, setCreateKey] = useState(0);
  const [editId, setEditId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>({ ...EMPTY });

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-announcements"],
    queryFn: listAnnouncements,
  });

  // Latest first.
  const sorted = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-announcements"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: Draft) =>
      createAnnouncement({ title: d.title, description: d.description, imageFile: d.imageFile }),
    onSuccess: () => {
      invalidate();
      toast.success("Posted to all members");
      setDraft({ ...EMPTY });
      setCreateKey((k) => k + 1);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (v: { id: string; d: Draft }) =>
      updateAnnouncement(v.id, {
        title: v.d.title,
        description: v.d.description,
        imageFile: v.d.imageFile,
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Announcement updated");
      setEditId(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });

  const send = () => {
    if (!draft.title.trim()) {
      return toast.error("Title required");
    }
    const description = isDescriptionEmpty(draft.description) ? "" : draft.description;
    createMut.mutate({ ...draft, description });
  };

  const openEdit = (a: (typeof list)[number]) => {
    setEditId(a.id);
    setEditDraft({
      title: a.title,
      description: a.description,
      image_url: a.image_url,
      imageFile: null,
    });
  };

  const saveEdit = () => {
    if (!editId) {
      return;
    }
    if (!editDraft.title.trim()) {
      return toast.error("Title required");
    }
    const description = isDescriptionEmpty(editDraft.description) ? "" : editDraft.description;
    updateMut.mutate({ id: editId, d: { ...editDraft, description } });
  };

  const del = (id: string) => {
    if (confirm("Delete this announcement?")) {
      deleteMut.mutate(id);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-serif text-2xl font-bold">Announcements</h2>
        <p className="text-sm text-muted-foreground">
          Post updates with title, attachment & description
        </p>
      </div>

      {/* Create */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
        <div className="text-sm font-semibold">Create Post</div>
        <PostFields key={createKey} value={draft} setValue={setDraft} />
        <div className="flex items-center justify-end">
          <button
            onClick={send}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
            disabled={createMut.isPending}
          >
            <Plus className="h-4 w-4" /> {createMut.isPending ? "Posting…" : "Post"}
          </button>
        </div>
      </div>

      {/* List / table */}
      <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Title</th>
                <th className="p-3">Description</th>
                <th className="p-3">Image</th>
                <th className="p-3">Post Date</th>
                <th className="p-3">Post Time</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">
                    Loading announcements…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-sm text-destructive">
                    {error instanceof Error ? error.message : "Failed to load announcements"}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && sorted.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">
                    No announcements yet
                  </td>
                </tr>
              )}
              {sorted.map((a) => (
                <tr key={a.id} className="border-t border-border align-top hover:bg-muted/40">
                  <td className="p-3">
                    <div className="font-semibold">{a.title}</div>
                  </td>
                  <td className="p-3 text-muted-foreground">
                    <p className="line-clamp-2 max-w-[320px]">
                      {toPlainText(a.description) || "—"}
                    </p>
                  </td>
                  <td className="p-3">
                    {a.image_url ? (
                      <a href={a.image_url} target="_blank" rel="noreferrer">
                        <img
                          src={a.image_url}
                          alt={a.title}
                          className="h-12 w-12 rounded-lg border border-border object-cover"
                        />
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <ImageIcon className="h-3 w-3" /> None
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    {fmtDate(a.created_at) || "—"}
                  </td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    {fmtTime(a.created_at) || "—"}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(a)}
                        title="Edit"
                        className="rounded-lg p-1.5 hover:bg-muted"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => del(a.id)}
                        title="Delete"
                        className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      {editId && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
          onClick={() => setEditId(null)}
        >
          <div
            className="mx-auto my-8 w-full max-w-2xl rounded-2xl bg-card p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-xl font-bold">Edit Announcement</h3>
              <button onClick={() => setEditId(null)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <PostFields value={editDraft} setValue={setEditDraft} />
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setEditId(null)}
                disabled={updateMut.isPending}
                className="btn-outline py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={updateMut.isPending}
                className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
              >
                {updateMut.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .inp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}
        .inp:focus{border-color:hsl(var(--primary))}
        /* Editor chrome themed to the app */
        .jgy-quill .ql-toolbar{border:1px solid hsl(var(--border));border-top-left-radius:.75rem;border-top-right-radius:.75rem;background:hsl(var(--background))}
        .jgy-quill .ql-container{border:1px solid hsl(var(--border));border-top:0;border-bottom-left-radius:.75rem;border-bottom-right-radius:.75rem;background:hsl(var(--background));font-family:inherit;font-size:.875rem}
        .jgy-quill .ql-editor{min-height:120px}
        .jgy-quill .ql-editor.ql-blank::before{color:hsl(var(--muted-foreground));font-style:normal}
      `}</style>
    </div>
  );
}

/** Shared post fields: title, rich-text description, image attach + preview. */
function PostFields({
  value,
  setValue,
}: {
  value: Draft;
  setValue: React.Dispatch<React.SetStateAction<Draft>>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Max 5MB");
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setValue((d) => ({ ...d, image_url: reader.result as string, imageFile: file }));
      setUploading(false);
      toast.success("Attachment ready");
    };
    reader.onerror = () => {
      setUploading(false);
      toast.error("Could not read file");
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      <input
        placeholder="Post title"
        className="inp text-base font-medium"
        value={value.title}
        onChange={(e) => setValue((d) => ({ ...d, title: e.target.value }))}
      />

      <div className="jgy-quill">
        <ReactQuill
          theme="snow"
          value={value.description}
          onChange={(html) => setValue((d) => ({ ...d, description: html }))}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Write a description..."
        />
      </div>

      {value.image_url && (
        <div className="relative inline-block">
          <img
            src={value.image_url}
            alt="attachment"
            className="h-32 w-auto rounded-lg border border-border object-cover"
          />
          <button
            onClick={() => {
              setValue((d) => ({ ...d, image_url: null, imageFile: null }));
              if (fileRef.current) {
                fileRef.current.value = "";
              }
            }}
            className="absolute -right-2 -top-2 rounded-full bg-destructive p-1 text-destructive-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <label className="flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm hover:bg-muted">
        <Paperclip className="h-4 w-4" />
        {uploading ? "Uploading..." : value.image_url ? "Replace image" : "Attach image"}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          disabled={uploading}
        />
      </label>
    </div>
  );
}
